import { useState, useRef, useCallback } from "react";
import { Helmet } from "react-helmet";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import {
  useGetGiftBoxStatusQuery,
  useOpenGiftBoxMutation,
  GiftBoxStatus,
  GiftBoxOpenResponse,
} from "../../features/treasury/treasurySlice";
import toast from "react-hot-toast";

function playGiftSound() {
  try {
    const audio = new Audio();
    audio.src = "data:audio/wav;base64,UklGRl9vT19XQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YUvvT18=";
    audio.volume = 0.3;
    audio.play().catch(() => {});
  } catch (_) {}
}

interface ResultModalProps {
  isOpen: boolean;
  onClose: () => void;
  result: GiftBoxOpenResponse | null;
}

function ResultModal({ isOpen, onClose, result }: ResultModalProps) {
  if (!result) return null;
  const isWin = result.won && result.amount != null;
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" />
        </Transition.Child>
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="bg-gray-900 border border-amber-500/30 rounded-2xl max-w-md w-full p-8 text-center shadow-xl shadow-amber-500/10">
                {isWin ? (
                  <>
                    <div className="text-6xl mb-4 animate-bounce">🎉</div>
                    <Dialog.Title className="text-2xl font-bold text-amber-400 mb-2">
                      GIFT BOX!
                    </Dialog.Title>
                    <p className="text-white/90 text-lg">
                      You got ₦{result.amount?.toLocaleString()} from the treasure chest! We've credited your GCBuying wallet.
                    </p>
                  </>
                ) : (
                  <>
                    <div className="text-5xl mb-4">📦</div>
                    <Dialog.Title className="text-xl font-bold text-gray-300 mb-2">
                      {result.message}
                    </Dialog.Title>
                  </>
                )}
                <button
                  onClick={onClose}
                  className="mt-6 w-full py-3 px-4 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-semibold transition-colors"
                >
                  OK
                </button>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

export const TreasuryChestPage = () => {
  const [resultModalOpen, setResultModalOpen] = useState(false);
  const [lastResult, setLastResult] = useState<GiftBoxOpenResponse | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const boxRef = useRef<HTMLButtonElement>(null);

  const { data: status, isLoading, refetch } = useGetGiftBoxStatusQuery();
  const [openGiftBox, { isLoading: isOpening }] = useOpenGiftBoxMutation();

  const handleOpen = useCallback(async () => {
    if (!status?.canOpen || isOpening || isAnimating) return;
    if (status.message && status.goldenBoxesLeft === 0) {
      toast.error(status.message);
      return;
    }
    setIsAnimating(true);
    playGiftSound();
    if (boxRef.current) {
      boxRef.current.classList.add("animate-shake", "ring-4", "ring-amber-400/50");
      setTimeout(() => {
        boxRef.current?.classList.remove("animate-shake", "ring-4", "ring-amber-400/50");
      }, 800);
    }
    try {
      const result = await openGiftBox().unwrap();
      setLastResult(result);
      setResultModalOpen(true);
      refetch();
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to open gift box");
    } finally {
      setIsAnimating(false);
    }
  }, [status, isOpening, isAnimating, openGiftBox, refetch]);

  const canTapOpen = status?.canOpen && !status?.message && status.goldenBoxesLeft !== 0;

  return (
    <>
      <Helmet>
        <title>Treasury Chest | GCBuying</title>
      </Helmet>
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-4 py-8">
        <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
          Treasury Chest
        </h1>
        <p className="text-white/60 text-sm md:text-base mb-8 text-center max-w-md">
          Open your free gift box every 4 days for a chance to win Naira credit!
        </p>

        {isLoading ? (
          <div className="flex flex-col items-center gap-4">
            <div className="w-32 h-32 rounded-2xl bg-white/10 animate-pulse" />
            <p className="text-white/60">Loading...</p>
          </div>
        ) : status?.enabled === false ? (
          <div className="bg-gray-800/50 border border-white/10 rounded-2xl p-8 max-w-md text-center">
            <div className="text-5xl mb-4">📦</div>
            <p className="text-white/90 text-lg">
              Daily Gift Box is currently unavailable.
            </p>
            <p className="text-white/60 text-sm mt-2">
              Check back later or contact support if you have questions.
            </p>
          </div>
        ) : !status?.canOpen ? (
          <div className="bg-gray-800/50 border border-white/10 rounded-2xl p-8 max-w-md text-center">
            <div className="text-5xl mb-4">⏳</div>
            <p className="text-white/80 mb-2">You've already opened your gift box for this period.</p>
            {status?.nextEligibleAt && (
              <p className="text-amber-400/90 text-sm">
                Next free box: {new Date(status.nextEligibleAt).toLocaleDateString(undefined, { dateStyle: "medium" })}
              </p>
            )}
          </div>
        ) : status?.message && status.goldenBoxesLeft === 0 ? (
          <div className="bg-gray-800/50 border border-white/10 rounded-2xl p-8 max-w-md text-center">
            <div className="text-5xl mb-4">📦</div>
            <p className="text-white/90 text-lg">
              All gift boxes claimed today. Better luck tomorrow!
            </p>
          </div>
        ) : (
          <div className="flex flex-col items-center">
            <button
              ref={boxRef}
              type="button"
              onClick={handleOpen}
              disabled={!canTapOpen || isOpening || isAnimating}
              className="relative w-40 h-40 md:w-48 md:h-48 rounded-2xl bg-gradient-to-br from-amber-600 to-amber-800 border-2 border-amber-400/50 flex items-center justify-center text-6xl md:text-7xl shadow-lg shadow-amber-500/20 hover:shadow-amber-500/30 hover:scale-105 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100 focus:outline-none focus:ring-4 focus:ring-amber-400/50"
            >
              🎁
            </button>
            <p className="mt-6 text-white/70 text-sm">
              Tap to open • {status?.goldenBoxesLeft ?? 0} golden box(es) left today
            </p>
            <p className="mt-2 text-amber-400/80 text-sm font-medium">
              {(isOpening || isAnimating) ? "Opening..." : "Tap the chest!"}
            </p>
          </div>
        )}

        <ResultModal
          isOpen={resultModalOpen}
          onClose={() => setResultModalOpen(false)}
          result={lastResult}
        />
      </div>

      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-8px); }
          40% { transform: translateX(8px); }
          60% { transform: translateX(-6px); }
          80% { transform: translateX(6px); }
        }
        .animate-shake {
          animation: shake 0.6s ease-in-out;
        }
      `}</style>
    </>
  );
};
